import { Box, Grid, Stack, Typography, useTheme } from "@mui/material";
import { MprcText } from "components";
import { MprcChoice } from "components/MprcChoice";
import { MprcPollComputed } from "components/MprcPoll";
import { AssCorrectionResult, AssessmentAlternativeDto, AssessmentInstanceDto, AssessmentMode, AssessmentPlayerMode, AssessmentQuestionDto, QuestionType } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ALTERNATIVE_TRUE } from "views/assessment/common/assessment";
import { useInstanceContext } from "views/assessment/player/AssPlayerBase";
import { AssRevisionResultIcon } from "views/assessment/player/common/AssCommon";
import { SurveyRevisionAnswersLine } from "views/assessment/player/common/SurveyRevisionAnswersLine";
import { UsersAnswerDrawer } from "views/assessment/player/common/UsersAnswerDrawer";

interface IProps {
    selectedItems: string[];
    viewData: AssessmentInstanceDto;
    question: AssessmentQuestionDto;
    deployId?: string;
    onChange: (selectedItems: string[]) => void;
}

export const AssPlayerMultipleChoice: React.FC<IProps> = ({ selectedItems, question, viewData, deployId, onChange }) => {

    const ctx = useInstanceContext();

    const { t } = useTranslation("assessment");
    const theme = useTheme();

    const [dialog, setDialog] = useState<any>();

    ///
    const selectChoice = (alternativeId: string) => {
        if (question!.questionType == QuestionType.SimpleChoice) {
            if (selectedItems.some(x => x == alternativeId))
                onChange([]);
            else
                onChange([alternativeId]);
        }
        else {
            if (selectedItems.some(x => x == alternativeId))
                onChange(selectedItems.filter(x => x != alternativeId));
            else
                onChange([...selectedItems, alternativeId]);
        }
    };

    ///
    const showAlternativeAnswers = (alternative: AssessmentAlternativeDto) => {
        setDialog(
            <UsersAnswerDrawer
                deployId={deployId!}
                instanceId={viewData.instanceId!}
                questionId={question.questionId}
                version={question.version}
                alternativeId={alternative.alternativeId}
                title={alternative.text}
                onClose={() => setDialog(null)}
            />
        );
    };

    ///
    const RenderAvailable = () => {
        const readOnly = viewData.playerMode == AssessmentPlayerMode.UserReview;
        return (
            <>
                {
                    question!.questionType == QuestionType.MultipleChoice &&
                    <Typography variant="h6" component="span" color={theme.palette.warning.main} marginBottom="4px">
                        <strong>{t("AssPlayer.Question.MultiChoiceText")}</strong>
                    </Typography>
                }
                <Stack direction="column" spacing={1}>
                    {
                        question.alternatives.map((v, index) => {
                            const isSelected = selectedItems.some(x => x == v.alternativeId);
                            return (
                                <Grid container key={index} alignItems="center" spacing={1}>
                                    <Grid size="grow">
                                        <MprcChoice
                                            text={v.text}
                                            color={isSelected ? theme.palette.common.white : theme.palette.text.primary}
                                            backgroundColor={isSelected ? theme.palette.highlight.main : "#d3dbeb"}
                                            onClick={readOnly ? undefined : () => selectChoice(v.alternativeId)}
                                        />
                                    </Grid>
                                </Grid>
                            );
                        })
                    }
                </Stack>
            </>
        );
    };

    ///
    const RenderEvalRevision = () => {
        return (
            <Stack direction="column" spacing={1}>
                {
                    question.questionType == QuestionType.MultipleChoice &&
                    question.revision?.isCorrect == false &&
                    question.revision?.alternatives.some(x => x.correctionResult == AssCorrectionResult.Correct) &&
                    <Typography variant="h5" textTransform="uppercase" color={theme.palette.error.main}>
                        {t("AssPlayer.Question.Revision.MultipleChoiceParcialErrorText")}
                    </Typography>
                }
                {
                    question.alternatives.map((v, index) => {
                        const isSelected = selectedItems.some(x => x == v.alternativeId);
                        const result = question.revision?.alternatives.find(x => x.alternativeId == v.alternativeId);
                        const backgroundColor = result?.correctionResult == AssCorrectionResult.Correct || result?.correctAnswer == ALTERNATIVE_TRUE
                            ? theme.palette.success.main
                            : result?.correctionResult == AssCorrectionResult.Wrong
                                ? theme.palette.error.main
                                : isSelected
                                    ? theme.palette.highlight.main
                                    : "#d3dbeb";
                        const fontColor = result?.correctionResult == AssCorrectionResult.Correct
                            || result?.correctionResult == AssCorrectionResult.Wrong
                            || result?.correctAnswer == ALTERNATIVE_TRUE
                            || isSelected
                            ? theme.palette.common.white
                            : theme.palette.text.primary;
                        return (
                            <Grid container key={index} alignItems="center" spacing={1}>
                                <Grid size="grow">
                                    <MprcChoice
                                        text={v.text}
                                        color={fontColor}
                                        backgroundColor={backgroundColor}
                                    />
                                </Grid>
                                <Grid size={{ xs: 0.7 }} textAlign="center">
                                    <AssRevisionResultIcon result={result?.correctionResult} />
                                </Grid>
                            </Grid>
                        );
                    })
                }
            </Stack>
        );
    };

    ///
    const RenderSurvRevision = () => {
        var maxValue = question.revision ? Math.max(...question.revision.alternatives.map(o => o.averageResult ?? 0)) : 0;

        return (
            <>
                <Stack direction="column" spacing={1}>
                    {
                        question.alternatives.map((v, index) => {
                            const isSelected = selectedItems.some(x => x == v.alternativeId);
                            const result = question.revision?.alternatives.find(x => x.alternativeId == v.alternativeId);
                            return (
                                <Grid container key={index} alignItems="center" spacing={1}>
                                    <Grid size="grow">
                                        <MprcPollComputed
                                            text={v.text}
                                            selected={isSelected}
                                            percent={result?.averageResult ?? 0}
                                            highlighted={maxValue > 0 && result?.averageResult == maxValue}
                                            size="medium"
                                            onClick={question.revision?.identifyAnswers == true ? () => showAlternativeAnswers(v) : undefined}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 0.7 }} textAlign="center">
                                        <Box sx={{ width: "45px", textAlign: "end" }}>
                                            <MprcText color={result?.averageResult === maxValue ? "success" : "inherit"}>{Formaters.number(result?.averageResult ?? 0, 1)}%</MprcText>
                                        </Box>
                                    </Grid>
                                </Grid>
                            );
                        })
                    }
                </Stack>
                <Box marginLeft={2} marginTop={2}>
                    {SurveyRevisionAnswersLine(viewData, question, t, ctx.deployId)}
                </Box>
            </>
        );
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {dialog}

            <Box>
                {
                    viewData.mode == AssessmentMode.Survey && viewData.playerMode == AssessmentPlayerMode.FinalResult
                        ? RenderSurvRevision()
                        : (viewData.mode == AssessmentMode.Evaluation && (viewData.playerMode == AssessmentPlayerMode.UserReview || viewData.playerMode == AssessmentPlayerMode.FinalResult))
                            ||
                            (viewData.mode == AssessmentMode.Challenge && question.revision != null)
                            ? RenderEvalRevision()
                            : RenderAvailable()
                }
            </Box>
        </>
    );
};
